import axios from "axios";
import { APP_URL } from "../../services/serviceConstants";
import {
  CLIENT_INOCHI,
  encodeBase64,
  clearStoredSession,
  getStoredSession,
} from "./mmtFlow";
import { postWebViewMessage } from "../../utlis/webViewBridge";
import { isSessionRejected, signInUrl } from "../../utlis/sessionRejected";
import { getActiveLocale, resolveLocale } from "../../configs/regionalProfile";
import { applyRegionFromUser } from "../../configs/activeRegion";
import { normalizeTimeline } from "./notifications";
import {
  COGNITIVE_DIAGNOSIS_FIELD,
  DIAGNOSIS_DATE_FIELD,
} from "../testPerson/cognitivePatientFields";

const api = axios.create({ baseURL: APP_URL });

api.interceptors.response.use(
  (response) => response,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    // Expired/invalid token (but not a failed login attempt): clear the
    // stored session and ask the user to log in again.
    if (isSessionRejected(status) && !url.includes("/v2/login")) {
      // Hard-coded "en" would have sent a Japanese user to an English login.
      // Clamped instead, so the redirect carries this market's active locale.
      const nextUrl = signInUrl(resolveLocale(getStoredSession()?.locale));
      clearStoredSession();
      sessionStorage.clear();
      postWebViewMessage("TOKEN_INVALID");
      window.location.href = nextUrl;
    }
    const data = err?.response?.data;
    const apiMessage =
      data && typeof data === "object"
        ? data.message || data.error?.message
        : null;
    const refined = new Error(apiMessage || err.message);
    refined.response = err.response;
    throw refined;
  },
);

const authConfig = (session) => ({
  headers: {
    Authorization: `Bearer ${session.accessToken}`,
    // Clamped: an out-of-market or not-yet-enabled locale must never reach
    // the API as a content-negotiation hint.
    "Accept-Language": getActiveLocale(),
    Accept: "application/json",
  },
});

export const loginMmt = async (email, password) => {
  const response = await api.post("/v2/login", {
    authFlow: "USER_PASSWORD_AUTH",
    userType: 1,
    username: email,
    password: encodeBase64(password),
  });
  const data = response.data;
  if (!data?.status || !data?.result?.accessToken) {
    const message =
      data?.error?.message || data?.message || "Invalid email or password.";
    const error = new Error(message);
    error.isPasswordExpired = /expired/i.test(message);
    throw error;
  }
  return {
    data,
    session: {
      accessToken: data.result.accessToken,
      staffId: data.userData?.id || "",
      providerId: data.metaData?.providerId || "",
      adminId: data.metaData?.adminId || "",
      clientId: data.metaData?.clientId || "",
      role: data.userData?.role || "",
      // Clamped on the way into the session so an out-of-market locale cannot
      // be persisted at login and read back as trusted later.
      locale: getActiveLocale(),
      client: CLIENT_INOCHI,
    },
  };
};

export const fetchLoggedUserInfo = async (session) => {
  const response = await api.get(
    `/users/${session.staffId}`,
    authConfig(session),
  );
  const userInfo = response.data?.data || null;
  // The users payload carries the account's `region` block (market, units,
  // name order, contact rules, locales). Apply it here — the one point every
  // screen's user fetch flows through — so Add, Edit and bulk-import Review all
  // resolve against the same region. A missing block defaults to US.
  applyRegionFromUser(userInfo);
  return userInfo;
};

export const fetchUserList = async ({
  session,
  page = 1,
  limit = 15,
  filter,
  sortField,
  sortOrder,
}) => {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (filter) {
    query.set("filterField", "firstName,lastName,test_person_id");
    query.set("filter", filter);
  }
  if (sortField) query.set("sortField", sortField);
  if (sortOrder) query.set("sortOrder", sortOrder);
  const response = await api.get(
    `/v3/seniors?${query.toString()}`,
    authConfig(session),
  );
  return response.data?.data || [];
};

export const fetchTimeline = async (session) => {
  const providerId = encodeURIComponent(session.staffId);
  const response = await api.get(
    `/v5/notification/all?providerId=${providerId}`,
    authConfig(session),
  );
  return normalizeTimeline(response.data?.data);
};

const toPatientPayload = (step1, step2) => {
  const data = [
    { uniqueId: "first_name", value: (step1.firstname || "").trim() },
    { uniqueId: "last_name", value: (step1.lastname || "").trim() },
    {
      // The create/update endpoint keys the customer-supplied external ID on
      // `test_person_id`. (Read paths may still surface it as
      // `user_id_from_client`; only the write payload uses this key.)
      uniqueId: "test_person_id",
      value: (step1.testPersonId || "").trim(),
    },
    { uniqueId: "gender", value: step1.gender },
    {
      uniqueId: "birth_year",
      value: step1.birthYear ? Number(step1.birthYear) : null,
    },
    {
      uniqueId: "birth_month",
      value: step1.birthMonth ? Number(step1.birthMonth) : null,
    },
  ];
  if (step1.cognitiveDiagnosis) {
    data.push({
      uniqueId: COGNITIVE_DIAGNOSIS_FIELD,
      value: step1.cognitiveDiagnosis,
    });
    if (step1.diagnosisDate) {
      data.push({ uniqueId: DIAGNOSIS_DATE_FIELD, value: step1.diagnosisDate });
    }
  }
  if (step2) {
    if (step2.locationId != null) {
      data.push({
        uniqueId: "location_id",
        value: String(step2.locationId).trim(),
      });
    }
    data.push({
      uniqueId: "zip_code",
      value: step2.zipcode ? String(step2.zipcode).trim() : "",
    });
    data.push({ uniqueId: "fitness_level", value: step2.exercises || "" });
    data.push({
      uniqueId: "email",
      value: step2.email ? String(step2.email).trim() : "",
    });
    data.push({
      uniqueId: "mobile_phone_number",
      value: step2.phone ? String(step2.phone).trim() : "",
    });
    data.push({
      uniqueId: "height",
      value: step2.height ? Number(step2.height) : null,
    });
    data.push({
      uniqueId: "weight",
      value: step2.weight ? Number(step2.weight) : null,
    });
    if (step2.injuries && step2.injuries.length) {
      data.push({ uniqueId: "injury_or_surgery", value: step2.injuries });
    }
    if (step2.medications && step2.medications.length) {
      data.push({ uniqueId: "medications", value: step2.medications });
    }
    if (step2.painSymptoms && step2.painSymptoms.length) {
      data.push({ uniqueId: "pain_symptoms", value: step2.painSymptoms });
    }
    // Blood Pressure Profile — optional self-reported field. Sent only when the
    // user actually selected a value, like the other optional fields above, so
    // that a create/update for someone without a BP profile matches the base
    // person contract (an unset field is omitted, never sent as null).
    if (step2.bloodPressure) {
      data.push({
        uniqueId: "blood_pressure_profile",
        value: step2.bloodPressure,
      });
    }
  }
  return { data };
};

export const addPatient = async (session, step1, step2) => {
  const response = await api.post(
    "/seniors",
    toPatientPayload(step1, step2),
    authConfig(session),
  );
  return response.data;
};

/**
 * External ID uniqueness preflight.
 *
 * Uses the EXISTING list endpoint, which already filters on `test_person_id`
 * (see API_CONTRACT GET /v3/seniors). No new endpoint is introduced.
 *
 * This is a convenience check over the records this account can see, not a
 * guarantee: another user on the same customer account may create the same ID
 * a moment later. `POST /seniors` rejection remains authoritative, and callers
 * must surface that rejection rather than treating a clean preflight as proof.
 * A failed preflight (network, permissions) returns `false` so it can never
 * block a legitimate create; the backend still decides.
 */
export const isExternalIdTaken = async (session, externalId) => {
  const value = String(externalId || "").trim();
  if (!value) return false;
  try {
    const query = new URLSearchParams({
      page: "1",
      limit: "15",
      filterField: "firstName,lastName,test_person_id",
      filter: value,
    });
    const response = await api.get(
      `/v3/seniors?${query.toString()}`,
      authConfig(session),
    );
    const rows = response.data?.data || [];
    return rows.some((r) => {
      const existing =
        r?.user_id_from_client ?? r?.test_person_id ?? r?.testPersonId ?? "";
      return String(existing).trim().toLowerCase() === value.toLowerCase();
    });
  } catch {
    return false; // never block on a preflight failure
  }
};

/**
 * Does a create failure mean "this External ID already exists"?
 * Read from the server's own response; nothing is inferred locally.
 */
export const isDuplicateIdError = (err) => {
  const status = err?.response?.status;
  const body = JSON.stringify(err?.response?.data || "").toLowerCase();
  return (
    status === 409 ||
    (status === 400 &&
      (body.includes("user_id_from_client") ||
        body.includes("already exists") ||
        body.includes("duplicate")))
  );
};

/* ------------------------------------------------------------------------
   Blood Pressure Profile
   ------------------------------------------------------------------------
   An optional, self-reported categorical value. Per the backend decision it
   is carried as an ordinary person field (`blood_pressure_profile`) inside the
   existing person payload — see `toPatientPayload` — and saved/loaded through
   the same POST/PUT/GET `/seniors` calls as every other field. No separate
   endpoint. "" / null means "not set", which is a valid default; it is never
   inferred from a vitals measurement.
   ------------------------------------------------------------------------ */

export const BLOOD_PRESSURE_VALUES = ["normal", "hypertension", "hypotension"];

export const getPatientDetails = async (session, seniorUuid) => {
  const response = await api.get(
    `/v4/seniors/${seniorUuid}`,
    authConfig(session),
  );
  // API may return { data: [...] } or { data: { data: [...] } }
  return response.data?.data ?? response.data ?? null;
};

export const updatePatient = async (session, seniorUuid, step1, step2) => {
  const payload = toPatientPayload(step1, step2);
  const response = await api.put(
    `/v4/seniors/${seniorUuid}`,
    payload,
    authConfig(session),
  );
  return response.data;
};

/**
 * Cognitive-account Edit Patient payload. Carries ONLY the cognitive form's
 * fields, so fields the form doesn't show (email, zip, BP, …) are never
 * overwritten. The Patient ID is read-only in Edit and is not sent.
 * `medications` is always sent: PUT /v4 replaces the stored list, so `[]` is
 * how a user clears it.
 */
const toCognitiveUpdatePayload = (step1, step2) => ({
  data: [
    { uniqueId: "first_name", value: (step1.firstname || "").trim() },
    { uniqueId: "last_name", value: (step1.lastname || "").trim() },
    { uniqueId: "gender", value: step1.gender },
    {
      uniqueId: "birth_year",
      value: step1.birthYear ? Number(step1.birthYear) : null,
    },
    {
      uniqueId: "birth_month",
      value: step1.birthMonth ? Number(step1.birthMonth) : null,
    },
    { uniqueId: "height", value: step2.height ? Number(step2.height) : null },
    { uniqueId: "weight", value: step2.weight ? Number(step2.weight) : null },
    { uniqueId: COGNITIVE_DIAGNOSIS_FIELD, value: step1.cognitiveDiagnosis },
    { uniqueId: DIAGNOSIS_DATE_FIELD, value: step1.diagnosisDate || "" },
    { uniqueId: "medications", value: step2.medications || [] },
  ],
});

export const updateCognitivePatient = async (
  session,
  seniorUuid,
  step1,
  step2,
) => {
  const response = await api.put(
    `/v4/seniors/${seniorUuid}`,
    toCognitiveUpdatePayload(step1, step2),
    authConfig(session),
  );
  return response.data;
};

export const removePatient = async (session, seniorUuid) => {
  const response = await api.delete(
    `/v2/seniors/${encodeURIComponent(seniorUuid)}?isHardDelete=false`,
    authConfig(session),
  );
  return response.data;
};

export const validatePartnerCode = async (partnerCode) => {
  const activeLocale = getActiveLocale();
  const partnerCodeLocale = ["ja", "zh"].includes(activeLocale)
    ? activeLocale
    : "en";
  const response = await api.get(
    `/v6/validate/channel-partner-code?channel_partner_code=${encodeURIComponent(partnerCode)}`,
    {
      headers: {
        "Accept-Language": partnerCodeLocale,
        Accept: "application/json",
      },
    },
  );
  if (!response.data?.status || !response.data?.data?.partner_code) {
    throw new Error(response.data?.message || "Invalid partner code.");
  }
  return response.data;
};

export const signupMmt = async (email) => {
  const response = await api.get(
    `/users/validate?email=${encodeURIComponent(email)}`,
  );
  if (!response.data?.status) {
    throw new Error(
      response.data?.message || "Failed to send verification code.",
    );
  }
  return response.data;
};

export const verifySignupOtp = async (email, otp) => {
  const response = await api.post("/users/validate", { email, otp });
  return response.data;
};

export const registerMmt = async ({
  email,
  password,
  firstName,
  lastName,
  partnerCode,
}) => {
  const response = await api.post("/users/submit", {
    appType: "seniorNavigator",
    appInfo: {
      first_name: firstName,
      last_name: lastName,
      email,
      password: encodeBase64(password),
      channel_partner_code: partnerCode,
    },
  });
  return response.data;
};

export const requestPasswordReset = async (email) => {
  const response = await api.post("/forgetPassword", { username: email });
  if (!response.data?.status) {
    throw new Error(response.data?.message || "Failed to send reset code.");
  }
  return response.data;
};

export const resetPassword = async ({ email, otp, password }) => {
  const response = await api.put("/forgetPassword", {
    code: otp,
    username: email,
    password: encodeBase64(password),
  });
  if (!response.data?.status) {
    throw new Error(response.data?.message || "Failed to reset password.");
  }
  return response.data;
};

export const logoutMmt = async (session) => {
  try {
    await api.post(
      "/logout",
      { globalSignOut: false, token: session.accessToken },
      authConfig(session),
    );
  } catch {
    // The local session should still be cleared if the server logout fails.
  }
};
